import { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Clock, CalendarClock, Loader2 } from "lucide-react";
import { useDueAlerts } from "@/hooks/use-due-alerts";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface DueAlertsWidgetProps {
  showToasts?: boolean;
}

export const DueAlertsWidget = ({ showToasts = true }: DueAlertsWidgetProps) => {
  const { alerts, loading } = useDueAlerts();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  // Show toast notifications on load
  useEffect(() => {
    if (!showToasts || loading) return;

    if (alerts.overdue.length > 0) {
      toast({
        variant: "destructive",
        title: `${alerts.overdue.length} conta${alerts.overdue.length > 1 ? "s" : ""} atrasada${alerts.overdue.length > 1 ? "s" : ""}`,
        description: `Total de ${formatCurrency(alerts.totalOverdue)} em atraso`,
      });
    } else if (alerts.dueToday.length > 0) {
      toast({
        title: `${alerts.dueToday.length} conta${alerts.dueToday.length > 1 ? "s" : ""} vence${alerts.dueToday.length > 1 ? "m" : ""} hoje`,
        description: `Total de ${formatCurrency(alerts.totalDueToday)} para hoje`,
      });
    }
  }, [loading, alerts.overdue.length, alerts.dueToday.length, showToasts]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const hasAlerts = alerts.overdue.length > 0 || alerts.dueToday.length > 0 || alerts.dueSoon.length > 0;

  if (!hasAlerts) {
    return null;
  }

  return (
    <div className="grid gap-3 grid-cols-1 md:grid-cols-3">
      {alerts.overdue.length > 0 && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Atrasadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {alerts.overdue.length}
            </div>
            <p className="text-sm text-muted-foreground">
              {formatCurrency(alerts.totalOverdue)}
            </p>
          </CardContent>
        </Card>
      )}

      {alerts.dueToday.length > 0 && (
        <Card className="border-warning/50 bg-warning/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-warning">
              <Clock className="h-4 w-4" />
              Vence Hoje
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">
              {alerts.dueToday.length}
            </div>
            <p className="text-sm text-muted-foreground">
              {formatCurrency(alerts.totalDueToday)}
            </p>
          </CardContent>
        </Card>
      )}

      {alerts.dueSoon.length > 0 && (
        <Card className="border-muted">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
              <CalendarClock className="h-4 w-4" />
              Próximos 3 dias
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {alerts.dueSoon.length}
            </div>
            <p className="text-sm text-muted-foreground">
              {formatCurrency(alerts.dueSoon.reduce((sum, a) => sum + a.amount, 0))}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
